import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { Dropdown } from 'src/components/molecules/Dropdown';
import Modal, { ModalVariant } from 'src/components/organisms/Modal';

import { NO_INCOME_REASON_OPTIONS } from './constants';

const ReasonField = styled.div`
  margin-top: 1rem;
  text-align: left;
`;

export interface CompleteWithoutIncomeModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onConfirm: (reasonCode: number) => void | Promise<unknown>;
}

interface CompleteWithoutIncomeFormValues {
  completionReason: string;
}

// ITL-2953: the confirm-only modal became a required-reason modal. Owns its own
// form context because the RHF Dropdown needs one and this renders outside the
// page's FormProvider. The Modal renders `text` OR `children`, so the copy lives
// in children alongside the select.
const CompleteWithoutIncomeModal = ({
  show,
  setShow,
  onConfirm
}: CompleteWithoutIncomeModalProps) => {
  const methods = useForm<CompleteWithoutIncomeFormValues>({
    defaultValues: { completionReason: '' }
  });
  const reason = useWatch({ control: methods.control, name: 'completionReason' });

  useEffect(() => {
    if (!show) {
      methods.reset();
    }
  }, [show, methods]);

  return (
    <Modal
      title="Complete report with no additional income?"
      showModal={show}
      variant={ModalVariant.none}
      labelButtonConfirm="Complete report"
      labelButtonCancel="Cancel"
      isDisabledButtonConfirm={!reason}
      onConfirm={async () => {
        setShow(false);

        await onConfirm(Number(reason));
      }}
      onCancel={() => setShow(false)}
    >
      <FormProvider {...methods}>
        <Text color={TextColor.gray600} variant={TextVariant.normal}>
          This completes the specialty review and sends the report without recording an income
          value. This can&apos;t be undone.
        </Text>
        <ReasonField>
          <Dropdown
            name="completionReason"
            placeholder="Select a reason"
            data={NO_INCOME_REASON_OPTIONS}
            listPosition="top"
          />
        </ReasonField>
      </FormProvider>
    </Modal>
  );
};

export default CompleteWithoutIncomeModal;
